package fr.frux.shared.domain.model

import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * Phase de saison derivee du referentiel especes, pour graduer l'affichage entre "on peut recolter
 * maintenant" et "silence radio" plutot qu'un simple booleen [HarvestWindow.isInSeason] : l'app veut
 * pouvoir dire "bientot mur" pour donner envie de revenir, pas seulement "pas de saison".
 */
enum class SeasonPhase { IN_SEASON, SOON, OFF_SEASON }

/**
 * Phase de saison au mois donne pour l'ensemble des fenetres d'une espece (toutes parties confondues,
 * cf. sureau : fleur ET fruit). Liste vide -> OFF_SEASON ; c'est aux appelants de distinguer en amont
 * l'absence de donnees d'une saison hors periode.
 */
fun List<HarvestWindow>.seasonPhase(month: Int): SeasonPhase {
    if (any { it.isInSeason(month) }) return SeasonPhase.IN_SEASON
    val nextMonth = month % 12 + 1
    if (any { it.isInSeason(nextMonth) }) return SeasonPhase.SOON
    return SeasonPhase.OFF_SEASON
}

/** Mois couverts par la fenetre, ordonnes de [HarvestWindow.monthStart] a [HarvestWindow.monthEnd] inclus, avec wrap. */
fun HarvestWindow.months(): List<Int> {
    if (monthStart <= monthEnd) return (monthStart..monthEnd).toList()
    return (monthStart..12).toList() + (1..monthEnd).toList()
}

/**
 * Mois "bientot mur" : celui qui precede immediatement le debut de chaque fenetre (wrap : janvier ->
 * decembre). Un mois deja couvert par une fenetre de la liste n'est jamais un soon month — "bientot
 * mur" ne doit jamais ecraser "en recolte" (cas des fenetres contigues, ex. sureau fleur 5-6 / fruit 7-8 :
 * juin est deja IN_SEASON, ce n'est pas un soon month pour la fenetre suivante).
 */
fun List<HarvestWindow>.soonMonths(): Set<Int> {
    val coveredMonths = flatMap { it.months() }.toSet()
    return mapNotNull { window ->
        val monthBefore = if (window.monthStart == 1) 12 else window.monthStart - 1
        monthBefore.takeIf { it !in coveredMonths }
    }.toSet()
}

/** Mois courant (1..12), horloge systeme dans le fuseau local — pas de saison "en UTC". */
@OptIn(ExperimentalTime::class)
@Suppress("DEPRECATION")
fun currentHarvestMonth(): Int =
    Clock.System
        .now()
        .toLocalDateTime(TimeZone.currentSystemDefault())
        .monthNumber

/**
 * Especes a mettre en avant « de saison en ce moment » (spec 2026-08-30, ADR 0020) : cueillables
 * ce mois-ci, triees par nom vernaculaire, dedupliquees par [Species.rollupId] pour qu'une variete
 * ne double pas son espece, puis plafonnees pour rester une rangee de pastilles et pas un
 * catalogue. Meme liste sur toutes les surfaces qui la consomment (carte, recoltes).
 */
fun List<Species>.inSeasonHighlights(
    month: Int,
    limit: Int = 8,
): List<Species> =
    filter { it.offeredForTrees && it.isInSeason(month) }
        .sortedBy { it.commonNameFr }
        .distinctBy { it.rollupId }
        .take(limit)

/**
 * Espece de saison a afficher, avec le nombre d'arbres connus autour (`null` = liste nationale,
 * sans arbres charges).
 */
data class SeasonalSpecies(
    val species: Species,
    val treeCount: Int?,
)

/**
 * Deux rangees de mise en avant "de saison" (#403, ADR 0020 : pas d'ecran dedie ni de
 * regionalisation serveur, la regionalisation reelle reste premium, ADR 0016). [isLocal] indique
 * si les listes refletent les arbres charges autour de l'utilisateur ou, a defaut, la liste
 * nationale de [inSeasonHighlights].
 */
data class SeasonalAround(
    val now: List<SeasonalSpecies>,
    val next: List<SeasonalSpecies>,
    val isLocal: Boolean,
)

/** Especes selectionnables dont la phase au mois donne correspond, dedupliquees par rollup, triees et plafonnees. */
private fun List<Species>.selectableInPhase(
    month: Int,
    phase: SeasonPhase,
    limit: Int,
): List<Species> =
    filter { it.offeredForTrees && it.harvests.seasonPhase(month) == phase }
        .sortedBy { it.commonNameFr }
        .distinctBy { it.rollupId }
        .take(limit)

/**
 * Mise en avant "de saison" croisant le referentiel avec les arbres deja charges par la carte
 * (#403) : pas d'ecran dedie (ADR 0020) ni de requete serveur dediee, seulement le lot deja en
 * memoire — biaise par ce que la carte a charge (plafonne a 1 000 arbres, cf. `MAP_TREE_LIMIT`) et
 * par la zone visitee, assume comme repli tant que la regionalisation reelle (premium, ADR 0016)
 * n'existe pas.
 *
 * Un arbre est regroupe sous le rollup de son espece ; une variete remonte donc sous son espece
 * (une chataigneraie ne se compte pas a part de son chataignier). Le representant affiche d'un
 * rollup est l'espece dont l'id est le rollup lui-meme si elle est selectionnable, sinon la
 * premiere espece selectionnable du rollup ; un rollup sans aucune espece selectionnable est
 * ignore (rien a afficher ni a cliquer).
 *
 * Repli national quand [now] et [next] locaux sont tous les deux vides (aucun arbre charge, ou
 * rien de saison dans le lot charge) : mêmes listes que la version nationale historique, avec
 * `treeCount = null` pour signaler l'absence de donnees locales.
 */
fun seasonalAround(
    species: List<Species>,
    trees: List<Tree>,
    month: Int,
    limit: Int = 8,
): SeasonalAround {
    val speciesById = species.associateBy { it.id }

    val treeCountByRollup: Map<Long, Int> =
        trees
            .mapNotNull { tree -> speciesById[tree.species.id]?.rollupId }
            .groupingBy { it }
            .eachCount()

    fun representativeOf(rollupId: Long): Species? {
        val inRollup = species.filter { it.rollupId == rollupId }
        return inRollup.find { it.id == rollupId && it.offeredForTrees }
            ?: inRollup.firstOrNull { it.offeredForTrees }
    }

    val localNow = mutableListOf<SeasonalSpecies>()
    val localNext = mutableListOf<SeasonalSpecies>()
    treeCountByRollup.forEach { (rollupId, count) ->
        val representative = representativeOf(rollupId) ?: return@forEach
        when (representative.harvests.seasonPhase(month)) {
            SeasonPhase.IN_SEASON -> localNow += SeasonalSpecies(representative, count)
            SeasonPhase.SOON -> localNext += SeasonalSpecies(representative, count)
            SeasonPhase.OFF_SEASON -> Unit
        }
    }

    fun sortAndLimit(items: List<SeasonalSpecies>): List<SeasonalSpecies> =
        items
            .sortedWith(
                compareByDescending<SeasonalSpecies> { it.treeCount ?: 0 }
                    .thenBy { it.species.commonNameFr },
            ).take(limit)

    val now = sortAndLimit(localNow)
    val next = sortAndLimit(localNext)

    if (now.isEmpty() && next.isEmpty()) {
        return SeasonalAround(
            now = species.inSeasonHighlights(month, limit).map { SeasonalSpecies(it, null) },
            next = species.selectableInPhase(month, SeasonPhase.SOON, limit).map { SeasonalSpecies(it, null) },
            isLocal = false,
        )
    }

    return SeasonalAround(now = now, next = next, isLocal = true)
}
